import React, { useState, useRef, useEffect } from 'react';
import { Button, Icon } from '@/components/common';
import { cn } from '@/utils/cn';
import { ScriptJobState } from '../hooks/useScriptJobStatus';
import { ScriptRepairPopover } from './ScriptRepairPopover';

interface ScriptJobStatusChipProps {
    jobState: ScriptJobState;
}

/**
 * Compact inline status chip in the ToolActionBar.
 * Shows current script import job status with contextual styling.
 * Clicking opens the repair popover for partial/failed jobs.
 */
export const ScriptJobStatusChip: React.FC<ScriptJobStatusChipProps> = ({ jobState }) => {
    const { status, failedPhases, error, isDismissed, dismiss } = jobState;
    const [showPopover, setShowPopover] = useState(false);
    const [successFading, setSuccessFading] = useState(false);
    const chipRef = useRef<HTMLButtonElement>(null);

    const [prevStatus, setPrevStatus] = useState<string | null>(status);
    if (status !== prevStatus) {
        setPrevStatus(status);
        if (status !== 'completed') {
            setSuccessFading(false);
        }
    }

    // Auto-fade success state after 10s
    useEffect(() => {
        if (status === 'completed' && !isDismissed) {
            const timer = setTimeout(() => {
                setSuccessFading(true);
                const fadeTimer = setTimeout(() => dismiss(), 500);
                return () => clearTimeout(fadeTimer);
            }, 10_000);
            return () => clearTimeout(timer);
        }
    }, [status, isDismissed, dismiss]);

    // Don't render if no job, dismissed, or queued
    if (!status || isDismissed || status === 'queued') return null;

    const isProcessing = status === 'processing' || status === 'stage_1' || status === 'stage_2' || status === 'repairing';
    const isPartial = status === 'partial';
    const isFailed = status === 'failed';
    const isClickable = isPartial || isFailed;

    // Status config
    const config = isProcessing ? {
        icon: 'progress_activity' as const,
        label: status === 'repairing' ? 'Repairing...' : 'Analyzing...',
        className: 'bg-blue-500/10 text-blue-600 dark:bg-blue-400/15 dark:text-blue-400',
        iconClassName: 'animate-spin',
    } : isPartial ? {
        icon: 'warning' as const,
        label: "Import incomplete",
        className: 'bg-amber-500/10 text-amber-600 dark:bg-amber-400/15 dark:text-amber-400 hover:bg-amber-500/20 cursor-pointer',
        iconClassName: '',
    } : isFailed ? {
        icon: 'error' as const,
        label: 'Import failed',
        className: 'bg-red-500/10 text-red-600 dark:bg-red-400/15 dark:text-red-400 hover:bg-red-500/20 cursor-pointer',
        iconClassName: '',
    } : {
        icon: 'check_circle' as const,
        label: 'Import complete',
        className: 'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-400/15 dark:text-emerald-400',
        iconClassName: '',
    };

    return (
        <div className="relative">
            <Button
                ref={chipRef}
                variant="ghost"
                size="sm"
                data-testid="script-job-status-chip"
                aria-label={`Script import status: ${config.label}`}
                onClick={() => isClickable && setShowPopover(!showPopover)}
                className={cn(
                    'min-h-0! gap-1.5 rounded-full! px-2.5! py-1! text-xs font-medium transition-all duration-200',
                    config.className,
                    successFading && 'opacity-0',
                    !isClickable && 'cursor-default'
                )}
                title={isPartial ? `${failedPhases.length} phase(s) need repair` : error || config.label}
            >
                <Icon 
                    name={config.icon} 
                    size="sm" 
                    className={config.iconClassName}
                />
                <span className="hidden sm:inline">{config.label}</span>
                {isClickable ? <Icon name="expand_more" size="xs" className={cn(
                        'transition-transform',
                        showPopover && 'rotate-180'
                    )} /> : null}
            </Button>

            {showPopover && (isPartial || isFailed) ? <ScriptRepairPopover
                    jobState={jobState}
                    onClose={() => setShowPopover(false)}
                    anchorRef={chipRef}
                /> : null}
        </div>
    );
};
